'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { Button } from './buttons/button'
import { Link, CustomLinkProps } from './link'
import React from 'react';
import { colors, semanticColors } from '../theme';

export function Dropdown(props: Headless.MenuProps) {
  return <Headless.Menu {...props} />
}

export function DropdownButton<T extends React.ElementType = typeof Button>({
  as = Button,
  ...props
}: { className?: string } & Omit<Headless.MenuButtonProps<T>, 'className'>) {
  return <Headless.MenuButton as={as} {...props} />
}

export function DropdownMenu({
  anchor = 'bottom',
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuItemsProps, 'className'>) {
  return (
    <Headless.MenuItems
      {...props}
      transition
      anchor={anchor}
      className={clsx(
        className,
        '[--anchor-gap:theme(spacing.2)] [--anchor-padding:theme(spacing.1)] data-[anchor~=start]:[--anchor-offset:-6px] data-[anchor~=end]:[--anchor-offset:6px] sm:data-[anchor~=start]:[--anchor-offset:-4px] sm:data-[anchor~=end]:[--anchor-offset:4px]',
        'isolate w-max rounded-xl p-1',
        'outline outline-1 outline-transparent focus:outline-none',
        'overflow-y-auto',
        'shadow-lg ring-1',
        'supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        'transition data-[closed]:data-[leave]:opacity-0 data-[leave]:duration-100 data-[leave]:ease-in'
      )}
      style={{
        backgroundColor: semanticColors.card.background,
        borderColor: semanticColors.card.border,
        boxShadow: semanticColors.card.shadow,
      }}
    />
  )
}

type DropdownItemLinkProps = Omit<CustomLinkProps, 'className'>;
type DropdownItemButtonProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'className' | 'type'>;

export function DropdownItem({
  className,
  children,
  ...restItemProps
}: {
  className?: string;
  children: React.ReactNode;
} & (DropdownItemLinkProps | DropdownItemButtonProps)) {
  const classes = clsx(
    className,
    'group cursor-default rounded-lg px-3.5 py-2.5 focus:outline-none sm:px-3 sm:py-1.5 transition-all duration-200',
    'text-left text-base/6 sm:text-sm/6',
    'data-[focus]:text-white',
    'data-[disabled]:opacity-50',
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
    '[&>[data-slot=icon]]:col-start-1 [&>[data-slot=icon]]:row-start-1 [&>[data-slot=icon]]:-ml-0.5 [&>[data-slot=icon]]:mr-2.5 [&>[data-slot=icon]]:size-5 sm:[&>[data-slot=icon]]:mr-2 [&>[data-slot=icon]]:sm:size-4',
    '[&>[data-slot=icon]]:data-[focus]:text-white',
    '[&>[data-slot=avatar]]:-ml-1 [&>[data-slot=avatar]]:mr-2.5 [&>[data-slot=avatar]]:size-6 sm:[&>[data-slot=avatar]]:mr-2 sm:[&>[data-slot=avatar]]:size-5'
  );

  const isLink = 'to' in restItemProps && (
        typeof (restItemProps as DropdownItemLinkProps).to === 'string' ||
        typeof (restItemProps as DropdownItemLinkProps).to === 'object'
    );

  return (
    <Headless.MenuItem>
      {isLink ? (
        <Link 
          {...(restItemProps as DropdownItemLinkProps)} 
          className={classes}
          style={{
            color: semanticColors.text.primary,
          }}
          data-focus-style={{
            backgroundColor: colors.secondary,
            color: semanticColors.text.inverted,
          }}
        >
          {children}
        </Link>
      ) : (
        <button 
          type="button" 
          {...(restItemProps as DropdownItemButtonProps)} 
          className={classes}
          style={{
            color: semanticColors.text.primary,
          }}
          data-focus-style={{
            backgroundColor: colors.secondary,
            color: semanticColors.text.inverted,
          }}
        >
          {children}
        </button>
      )}
    </Headless.MenuItem>
  );
}

export function DropdownHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={clsx(className, 'col-span-5 px-3.5 pb-1 pt-2.5 sm:px-3')} />
}

export function DropdownSection({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSectionProps, 'className'>) {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        'col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]'
      )}
    />
  )
}

export function DropdownHeading({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuHeadingProps, 'className'>) {
  return (
    <Headless.MenuHeading
      {...props}
      className={clsx(
        className,
        'col-span-full grid grid-cols-[1fr,auto] gap-x-12 px-3.5 pb-1 pt-2 text-sm/5 font-medium sm:px-3 sm:text-xs/5'
      )}
      style={{ color: semanticColors.text.secondary }}
    />
  )
}

export function DropdownDivider({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSeparatorProps, 'className'>) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'col-span-full mx-3.5 my-1 h-px border-0 sm:mx-3'
      )}
      style={{ backgroundColor: semanticColors.card.border }}
    />
  )
}

export function DropdownLabel({
  className,
  ...props
}: { className?: string } & Omit<Headless.LabelProps, 'className'>) {
  return (
    <Headless.Label {...props} data-slot="label" className={clsx(className, 'col-start-2 row-start-1')} />
  )
}

export function DropdownDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'className'>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'col-span-2 col-start-2 row-start-2 text-sm/5 sm:text-xs/5 group-data-[focus]:text-white'
      )}
      style={{ color: semanticColors.text.secondary }}
    />
  )
}

export function DropdownShortcut({
  keys,
  className,
  ...props
}: { keys: string | string[]; className?: string } & Omit<Headless.DescriptionProps<'kbd'>, 'className'>) {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(className, 'col-start-5 row-start-1 flex justify-self-end')}
    >
      {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
        <kbd
          key={index}
          className={clsx([
            'min-w-[2ch] text-center font-sans capitalize group-data-[focus]:text-white',
            index > 0 && char.length > 1 && 'pl-1',
          ])}
          style={{ color: semanticColors.text.tertiary }}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  )
}